import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FiPlus, FiTrash2, FiChevronDown, FiChevronUp, FiBriefcase } from "react-icons/fi";
import { cn } from "@/lib/utils";
import type { EmploymentData, EmploymentDetails, EmploymentType, EmployerType } from "@/types/fact-finder";

interface EmploymentSectionProps {
  data: EmploymentData | null;
  hasApplicant2: boolean;
  onSave: (data: EmploymentData) => Promise<void>;
  onNext: () => void;
}

const defaultEmployment: EmploymentDetails = {
  employmentType: "full_time",
  employerName: "",
  address: "",
  suburb: "",
  state: "",
  postcode: "",
  country: "Australia",
  startDate: "",
  contactPerson: "",
  contactPhone: "",
  employerType: "private_sector",
  occupation: "",
  isOnProbation: false,
  probationRemaining: "",
};

const defaultData: EmploymentData = {
  applicant1: {
    current: { ...defaultEmployment },
    previous: [],
  },
};

const employmentTypes: { value: EmploymentType; label: string }[] = [
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "casual", label: "Casual" },
  { value: "contract", label: "Contract" },
  { value: "self_employed", label: "Self Employed" },
  { value: "home_duties", label: "Home Duties" },
];

const employerTypes: { value: EmployerType; label: string }[] = [
  { value: "public_sector", label: "Public Sector" },
  { value: "private_sector", label: "Private Sector" },
];

export function EmploymentSection({ data, hasApplicant2, onSave, onNext }: EmploymentSectionProps) {
  const [formData, setFormData] = useState<EmploymentData>(() => {
    const initial = data || defaultData;
    if (hasApplicant2 && !initial.applicant2) {
      return { ...initial, applicant2: { current: { ...defaultEmployment }, previous: [] } };
    }
    return initial;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // Start with current employment sections expanded
    const initial = new Set(["applicant1-current"]);
    if (hasApplicant2) {
      initial.add("applicant2-current");
    }
    return initial;
  });

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  const getSectionKey = (applicant: string, type: string, index: number | null) => {
    return `${applicant}-${type}${index !== null ? `-${index}` : ""}`;
  };

  const formatDateRange = (startDate: string, endDate?: string) => {
    const formatDate = (d: string) => {
      if (!d) return "";
      const date = new Date(d);
      return date.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
    };
    const start = formatDate(startDate);
    const end = endDate ? formatDate(endDate) : "Present";
    return start ? `${start} - ${end}` : "";
  };

  const isEmploymentFilled = (emp: EmploymentDetails) => {
    return !!(emp.employerName && emp.occupation && emp.startDate);
  };

  const updateEmployment = (
    applicant: "applicant1" | "applicant2",
    type: "current" | "previous",
    index: number | null,
    field: string,
    value: unknown
  ) => {
    setFormData((prev) => {
      const applicantData = prev[applicant];
      if (!applicantData) return prev;

      if (type === "current") {
        return {
          ...prev,
          [applicant]: {
            ...applicantData,
            current: { ...applicantData.current, [field]: value },
          },
        };
      } else if (index !== null) {
        const newPrevious = [...applicantData.previous];
        newPrevious[index] = { ...newPrevious[index], [field]: value };
        return {
          ...prev,
          [applicant]: { ...applicantData, previous: newPrevious },
        };
      }
      return prev;
    });
  };

  const addPreviousEmployment = (applicant: "applicant1" | "applicant2") => {
    setFormData((prev) => {
      const applicantData = prev[applicant];
      if (!applicantData) return prev;
      return {
        ...prev,
        [applicant]: {
          ...applicantData,
          previous: [...applicantData.previous, { ...defaultEmployment }],
        },
      };
    });
  };

  const removePreviousEmployment = (applicant: "applicant1" | "applicant2", index: number) => {
    setFormData((prev) => {
      const applicantData = prev[applicant];
      if (!applicantData) return prev;
      return {
        ...prev,
        [applicant]: {
          ...applicantData,
          previous: applicantData.previous.filter((_, i) => i !== index),
        },
      };
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const { applicant1, applicant2 } = formData;

    if (!applicant1.current.employerName.trim()) {
      newErrors["a1_employerName"] = "Employer name is required";
    }
    if (!applicant1.current.occupation.trim()) {
      newErrors["a1_occupation"] = "Occupation is required";
    }
    if (!applicant1.current.startDate) {
      newErrors["a1_startDate"] = "Start date is required";
    }

    if (hasApplicant2 && applicant2) {
      if (!applicant2.current.employerName.trim()) {
        newErrors["a2_employerName"] = "Employer name is required";
      }
      if (!applicant2.current.occupation.trim()) {
        newErrors["a2_occupation"] = "Occupation is required";
      }
      if (!applicant2.current.startDate) {
        newErrors["a2_startDate"] = "Start date is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      await onSave(formData);
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  const renderEmploymentForm = (
    applicant: "applicant1" | "applicant2",
    type: "current" | "previous",
    employment: EmploymentDetails,
    index: number | null = null,
    title: string
  ) => {
    const prefix = `${applicant === "applicant1" ? "a1" : "a2"}_${type === "current" ? "" : `prev${index}_`}`;
    const sectionKey = getSectionKey(applicant, type, index);
    const isExpanded = expandedSections.has(sectionKey);
    const isFilled = isEmploymentFilled(employment);
    const dateRange = formatDateRange(employment.startDate, employment.endDate);

    return (
      <div key={`${applicant}-${type}-${index}`} className="bg-muted/30 rounded-lg overflow-hidden">
        {/* Collapsible Header */}
        <button
          type="button"
          onClick={() => toggleSection(sectionKey)}
          className={cn(
            "w-full flex items-center justify-between p-4 text-left transition-colors",
            "hover:bg-muted/50",
            !isExpanded && isFilled && "bg-muted/20"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-full",
              isFilled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            )}>
              <FiBriefcase className="size-4" />
            </div>
            <div>
              <h4 className="font-medium">{title}</h4>
              {!isExpanded && isFilled && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {employment.employerName} • {employment.occupation}
                  {dateRange && ` • ${dateRange}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {type === "previous" && index !== null && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  removePreviousEmployment(applicant, index);
                }}
                className="text-destructive hover:text-destructive"
              >
                <FiTrash2 className="size-4" />
              </Button>
            )}
            {isExpanded ? (
              <FiChevronUp className="size-5 text-muted-foreground" />
            ) : (
              <FiChevronDown className="size-5 text-muted-foreground" />
            )}
          </div>
        </button>

        {/* Collapsible Content */}
        <div className={cn(
          "overflow-hidden transition-all duration-200",
          isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        )}>
          <div className="p-4 pt-0 space-y-4">

        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Employment Type</label>
            <Select
              value={employment.employmentType}
              onValueChange={(v) =>
                updateEmployment(applicant, type, index, "employmentType", v as EmploymentType)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {employmentTypes.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Employer Name <span className="text-destructive">*</span>
            </label>
            <Input
              value={employment.employerName}
              onChange={(e) => updateEmployment(applicant, type, index, "employerName", e.target.value)}
              className={errors[`${prefix}employerName`] ? "border-destructive" : ""}
            />
            {errors[`${prefix}employerName`] && (
              <p className="text-xs text-destructive">{errors[`${prefix}employerName`]}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Occupation <span className="text-destructive">*</span>
            </label>
            <Input
              value={employment.occupation}
              onChange={(e) => updateEmployment(applicant, type, index, "occupation", e.target.value)}
              className={errors[`${prefix}occupation`] ? "border-destructive" : ""}
            />
            {errors[`${prefix}occupation`] && (
              <p className="text-xs text-destructive">{errors[`${prefix}occupation`]}</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Employer Type</label>
            <Select
              value={employment.employerType}
              onValueChange={(v) =>
                updateEmployment(applicant, type, index, "employerType", v as EmployerType)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {employerTypes.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Start Date <span className="text-destructive">*</span>
            </label>
            <Input
              type="date"
              value={employment.startDate}
              onChange={(e) => updateEmployment(applicant, type, index, "startDate", e.target.value)}
              className={errors[`${prefix}startDate`] ? "border-destructive" : ""}
            />
            {errors[`${prefix}startDate`] && (
              <p className="text-xs text-destructive">{errors[`${prefix}startDate`]}</p>
            )}
          </div>
        </div>

        {type === "previous" && (
          <div className="space-y-2 max-w-xs">
            <label className="text-sm font-medium">End Date</label>
            <Input
              type="date"
              value={employment.endDate || ""}
              onChange={(e) => updateEmployment(applicant, type, index, "endDate", e.target.value)}
            />
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium">Employer Address</label>
          <Input
            value={employment.address}
            onChange={(e) => updateEmployment(applicant, type, index, "address", e.target.value)}
            placeholder="Street address"
          />
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Suburb</label>
            <Input
              value={employment.suburb}
              onChange={(e) => updateEmployment(applicant, type, index, "suburb", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">State</label>
            <Select
              value={employment.state}
              onValueChange={(v) => updateEmployment(applicant, type, index, "state", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NSW">NSW</SelectItem>
                <SelectItem value="VIC">VIC</SelectItem>
                <SelectItem value="QLD">QLD</SelectItem>
                <SelectItem value="SA">SA</SelectItem>
                <SelectItem value="WA">WA</SelectItem>
                <SelectItem value="TAS">TAS</SelectItem>
                <SelectItem value="NT">NT</SelectItem>
                <SelectItem value="ACT">ACT</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Postcode</label>
            <Input
              value={employment.postcode}
              onChange={(e) => updateEmployment(applicant, type, index, "postcode", e.target.value)}
              maxLength={4}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Country</label>
            <Input
              value={employment.country}
              onChange={(e) => updateEmployment(applicant, type, index, "country", e.target.value)}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Contact Person</label>
            <Input
              value={employment.contactPerson}
              onChange={(e) => updateEmployment(applicant, type, index, "contactPerson", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Contact Phone</label>
            <Input
              type="tel"
              value={employment.contactPhone}
              onChange={(e) => updateEmployment(applicant, type, index, "contactPhone", e.target.value)}
            />
          </div>
        </div>

        {type === "current" && (
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={employment.isOnProbation}
                onChange={(e) =>
                  updateEmployment(applicant, type, index, "isOnProbation", e.target.checked)
                }
                className="w-4 h-4"
              />
              <span className="text-sm">Currently on probation?</span>
            </label>

            {employment.isOnProbation && (
              <div className="flex items-center gap-2">
                <label className="text-sm">Time remaining:</label>
                <Input
                  value={employment.probationRemaining}
                  onChange={(e) =>
                    updateEmployment(applicant, type, index, "probationRemaining", e.target.value)
                  }
                  placeholder="e.g. 3 months"
                  className="w-32"
                />
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">Employment Details</h2>
        <p className="text-muted-foreground mt-1">
          Provide current and previous employment history for all applicants.
        </p>
      </div>

      {/* Applicant 1 Employment */}
      <div className="space-y-6">
        <h3 className="text-lg font-medium border-b pb-2">Applicant 1</h3>

        {renderEmploymentForm("applicant1", "current", formData.applicant1.current, null, "Current Employment")}

        {formData.applicant1.previous.map((emp, idx) =>
          renderEmploymentForm("applicant1", "previous", emp, idx, `Previous Employment ${idx + 1}`)
        )}

        <Button
          variant="outline"
          onClick={() => addPreviousEmployment("applicant1")}
          className="gap-2"
        >
          <FiPlus className="size-4" />
          Add Previous Employment
        </Button>
      </div>

      {/* Applicant 2 Employment */}
      {hasApplicant2 && formData.applicant2 && (
        <div className="space-y-6">
          <h3 className="text-lg font-medium border-b pb-2">Applicant 2</h3>

          {renderEmploymentForm("applicant2", "current", formData.applicant2.current, null, "Current Employment")}

          {formData.applicant2.previous.map((emp, idx) =>
            renderEmploymentForm("applicant2", "previous", emp, idx, `Previous Employment ${idx + 1}`)
          )}

          <Button
            variant="outline"
            onClick={() => addPreviousEmployment("applicant2")}
            className="gap-2"
          >
            <FiPlus className="size-4" />
            Add Previous Employment
          </Button>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? "Saving..." : "Save & Continue"}
        </Button>
      </div>
    </div>
  );
}
